import { useState } from 'react';
import { Link, notFound, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { meQuery, orderQuery, orderSourcesQuery } from '@/queries';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';

import { GarmentMockup } from '@/components/garment-mockup';
import { PhotoViewer, type Photo } from '@/components/photo-viewer';
import { OrderStatusBadge, StatusTimeline, formatDate } from '@/components/status-timeline';
import { StoreBadges } from '@/components/store-badges';
import { OrderLines } from '@/components/admin/order-lines';
import { formatCents } from '@/lib/money';
import { readAddress, type AddressLike } from '@/lib/address';
import type { GarmentType } from '@/lib/types';

/**
 * The route. It reads the URL and nothing else.
 *
 * The BODY takes its reference as a prop, because the same facts are now drawn
 * in two places: on this page, and in the panel beside the order register. A
 * component that reads `useParams` can only ever be the whole page.
 */
export default function OrderDetailPage() {
  const { ref } = useParams({ strict: false }) as { ref: string };
  const search = useSearch({ strict: false }) as { new?: string; t?: string };
  return <OrderDetail refValue={ref} token={search.t} justPlaced={search.new === '1'} />;
}

export interface OrderDetailProps {
  refValue: string;
  /** The share token from a confirmation link. R-154. */
  token?: string;
  /** Draw the "Order confirmed" banner. Only ever true straight after paying. */
  justPlaced?: boolean;
  /**
   * Drawn beside the register rather than on its own page.
   *
   * It drops the page furniture: the outer width cap, the page padding and the
   * "All orders" link, all of which are wrong inside a panel that is already
   * next to the list of all orders.
   */
  embedded?: boolean;
}

export function OrderDetail({
  refValue: ref, token, justPlaced = false, embedded = false,
}: OrderDetailProps) {
  // The token from the link. The page read the ref alone and was a 404 for
  // every guest, which is what the customer sees after paying (R-154).
  const { order, config } = useSuspenseQuery(orderQuery(ref, token)).data;
  if (!order) throw notFound();

  /**
   * Back to the list the reader CAME FROM. R-258.
   *
   * This always pointed at `/orders`, the customer's own orders. A staff member
   * opens an order from the register at `/admin/orders`, presses the only link
   * on the page that looks like a way back, and lands on a list of the orders
   * they personally bought, which is empty. It reads as though the register
   * lost their work.
   *
   * A guest holding a share token gets no link at all. They have one order and
   * no list, and `/orders` would bounce them to a sign-in form they have no
   * account for.
   */
  const me = useSuspenseQuery(meQuery).data;
  const backTo = me.isInternal ? '/admin/orders' : '/orders';

  /**
   * BACK, not "go to the list". R-258 fixed which list; this fixes the word
   * "back".
   *
   * A link to `/admin/orders` is a forward navigation to a fresh register: the
   * top of page one of everything, no filter, no search. Somebody working
   * through the awaiting-payment orders on page three lost their place on
   * every single one.
   *
   * Going back through the history instead restores the URL the register was
   * at, and the register now keeps its filter, its page and its search box in
   * that URL. The router's `scrollRestoration` puts the page back where it was
   * on its own.
   *
   * The link stays underneath for the two cases history cannot answer: a
   * reader who arrived from a confirmation email, and one who typed the
   * reference straight in. Both have no register to go back to.
   */
  const router = useRouter();
  const cameFromAList = typeof window !== 'undefined' && window.history.length > 1;

  /**
   * Do the money lines add up? R-289.
   *
   * On an order this system placed, they always do: `subtotal_cents` is GST
   * inclusive and `subtotal + shipping = total`, with `gst_cents` as the
   * component inside.
   *
   * On the 12,656 orders imported from Shopify they often do not, and 5,260 of
   * them are in that state. Two different things cause it and both are real:
   *
   *   - Shopify's `subtotal_price` is before tax when the shop does not
   *     include tax in its prices, and after when it does. The setting changed
   *     over six years, so both conventions are in the history.
   *   - `total_price` is what the order stands at NOW, after any refund, while
   *     the goods and shipping figures are what was recorded at the sale. An
   *     order whose shipping was refunded reads goods 87.31, shipping 20.59,
   *     total 87.31.
   *
   * The screen showed all three and let the reader do the arithmetic, which
   * makes it look as though the system has lost money. Rewriting the client's
   * historical figures to make them add up would destroy the difference
   * between the two causes, so the panel says which figures are which instead.
   */
  const partsAddUp = order.subtotal_cents + order.shipping_cents === order.total_cents;

  // Either shape, read once. See `src/lib/address.ts`.
  const shipTo = readAddress(order.shipping_address as AddressLike);

  return (
    <div
      className={
        embedded
          // No width cap and no `mx-auto`: the panel is already the right
          // width, and a second cap inside it would centre a narrow column in
          // a narrow column.
          ? 'px-5 py-5 @container'
          : 'mx-auto max-w-[1180px] px-[30px] py-8 @container'
      }
    >
      {!embedded && (me.isInternal || me.user) && (
        cameFromAList ? (
          <button
            type="button"
            onClick={() => router.history.back()}
            className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
          >
            <ArrowLeft size={16} weight="bold" aria-hidden />
            {me.isInternal ? 'All orders' : 'Your orders'}
          </button>
        ) : (
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 py-2 text-sm font-semibold text-muted hover:text-ink"
          >
            <ArrowLeft size={16} weight="bold" aria-hidden />
            {me.isInternal ? 'All orders' : 'Your orders'}
          </Link>
        )
      )}

      {justPlaced && (
        <div className="mt-4 flex items-start gap-3 rounded-[10px] border border-ok/30 bg-ok-wash p-5">
          <CheckCircle size={24} weight="fill" className="mt-0.5 shrink-0 text-ok" aria-hidden />
          <div>
            <p className="text-lg font-bold">Order confirmed</p>
            <p className="mt-1 text-[15px]">
              Thanks. Your reference is{' '}
              <span className="font-mono font-bold">{order.ref}</span> and we have emailed
              you a receipt.
            </p>
          </div>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-4 @3xl:mt-6">
        <div>
          <p className="font-mono text-sm font-bold text-brand-ink">{order.ref}</p>
          {/* Smaller in the panel, full size on the page. Same container
              query as the layout below, so the two never disagree. */}
          <h1 className="mt-1 text-[22px] font-semibold tracking-[-0.02em] @3xl:text-[32px]">
            Order {order.ref}
          </h1>
          <p className="mt-1 text-[15px] text-muted">
            Placed {formatDate(order.created_at)}
          </p>
        </div>
        <OrderStatusBadge status={order.status} />
      </div>

      {/*
        * `@3xl`, not `lg`. A container query, so this reads the width of the
        * box it is in rather than the width of the window. In the panel beside
        * the register the box is about 460px and this stays one column; on its
        * own page the box is 1180px and it splits. A viewport breakpoint gave
        * the narrow panel a two column layout on a wide screen, which is the
        * exact bug `split-view.tsx` exists to remove.
        */}
      <div className="mt-6 grid gap-8 @3xl:mt-8 @3xl:gap-10 @3xl:grid-cols-[minmax(0,1fr)_360px] @3xl:items-start">
        {/* min-w-0: a grid item is as wide as its widest child by default, and the
            lines table would push the panel past a phone screen (tracker #8). */}
        <div className="min-w-0 space-y-10">
          {/* Staff only. Spec 011, FR-1140: product, colour and size in their
              own columns, and the customer's page does not change (FR-1143).
              First, because it is the view staff work from (Guy, 29 October). */}
          {embedded && (
            <section aria-labelledby="stock-lines-heading">
              <h2 id="stock-lines-heading" className="mb-3 text-lg font-bold tracking-[-0.02em]">
                Lines as stock
              </h2>
              <OrderLines orderId={order.id} />
            </section>
          )}

          <section aria-labelledby="items-heading">
            <h2 id="items-heading" className="mb-4 text-lg font-bold tracking-[-0.02em]">
              What you ordered
            </h2>
            <ul className="space-y-3">
              {order.items.map((item) => {
                const config = (item.config ?? {}) as Record<string, unknown>;
                return (
                  <li
                    key={item.id}
                    className="flex items-center gap-4 rounded-[10px] border border-line bg-panel p-4"
                  >
                    {item.photos && item.photos.length > 0 ? (
                      <LinePhoto photos={item.photos} title={item.title} />
                    ) : (
                    <div className="w-20 shrink-0 rounded-[10px] bg-raised p-1.5">
                      <GarmentMockup
                        // `other`, not `tee`. An imported Shopify line carries no garment type,
                        // and guessing draws a crop top and a pair of shorts as two
                        // identical t-shirts. R-251.
                        type={(config.type as GarmentType) ?? 'other'}
                        colorHex={(config.color_hex as string) ?? '#cfd3d8'}
                        designUrl={(config.design_url as string) ?? null}
                        placement={{
                          pos_x: (config.pos_x as number) ?? 50,
                          pos_y: (config.pos_y as number) ?? 48,
                          scale: (config.scale as number) ?? 60,
                        }}
                        label={item.title}
                        className="aspect-square w-full"
                      />
                    </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-bold">{item.title}</p>
                      <p className="text-sm text-muted">
                        {[config.color, config.size].filter(Boolean).join(' · ')} &middot; x
                        {item.qty}
                      </p>
                    </div>
                    <p className="shrink-0 font-bold">{formatCents(item.line_total_cents)}</p>
                  </li>
                );
              })}
            </ul>
          </section>

          <section aria-labelledby="timeline-heading">
            <h2 id="timeline-heading" className="mb-5 text-lg font-bold tracking-[-0.02em]">
              Progress
            </h2>
            <StatusTimeline events={order.events} />
          </section>
        </div>

        <aside className="space-y-6">
          <div className="rounded-[10px] border border-line bg-panel p-5">
            <h2 className="text-sm font-bold">Payment</h2>
            <dl className="mt-4 space-y-2.5 text-[15px]">
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Goods</dt>
                <dd className="font-semibold">{formatCents(order.subtotal_cents)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted">Shipping</dt>
                <dd className="font-semibold">
                  {order.shipping_cents === 0 ? 'Free' : formatCents(order.shipping_cents)}
                </dd>
              </div>
              <div className="flex justify-between gap-4 border-t border-line pt-3 text-lg">
                <dt className="font-bold">Total</dt>
                <dd className="font-extrabold">{formatCents(order.total_cents)}</dd>
              </div>
              {/* When the parts do not reach the total, say so. R-289. */}
              {!partsAddUp && (
                <p className="pt-1 text-[12.5px] leading-relaxed text-muted">
                  Goods and shipping are the figures Shopify recorded when this
                  order was placed. The total is what the order stands at now,
                  so the three do not add up on an order that was later refunded
                  or adjusted.
                </p>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-[13px] text-muted">GST included</dt>
                <dd className="text-[13px] text-muted">{formatCents(order.gst_cents)}</dd>
              </div>
              <div className="flex justify-between gap-4 pt-1">
                <dt className="text-[13px] text-muted">Settled by</dt>
                <dd className="text-[13px] text-muted">
                  {order.settlement.replace('_', ' ')}
                </dd>
              </div>
              {me.isInternal && order.source && <CameInFrom code={order.source} />}
            </dl>
          </div>

          <div className="rounded-[10px] border border-line bg-panel p-5">
            <h2 className="text-sm font-bold">Shipping to</h2>
            {/**
              * Read through `readAddress`, because there are two shapes in this
              * column. R-303.
              *
              * This block named `line1`, `suburb`, `state` and `postcode`,
              * which is what checkout writes here. Every one of the 12,656
              * imported orders holds Shopify's shape instead, so the card was
              * BLANK on all of them: a heading with nothing under it, on the
              * screen somebody opens to find out where to send a repeat order.
              * The address was there the whole time under other keys.
              */}
            {shipTo.hasAnything ? (
              <address className="mt-3 text-[15px] not-italic leading-relaxed text-muted">
                {shipTo.name && (
                  <>
                    <span className="font-semibold text-ink">{shipTo.name}</span>
                    <br />
                  </>
                )}
                {shipTo.street.map((line) => (
                  <span key={line}>
                    {line}
                    <br />
                  </span>
                ))}
                {shipTo.locality}
                {shipTo.country && (
                  <>
                    <br />
                    {shipTo.country}
                  </>
                )}
                {shipTo.phone && (
                  <>
                    <br />
                    {shipTo.phone}
                  </>
                )}
              </address>
            ) : (
              /* 601 imported orders carry no address at all. That is a fact
                 about a pickup or a cancelled order, and it reads as one. */
              <p className="mt-3 text-[13px] leading-relaxed text-muted">
                No delivery address was recorded for this order.
              </p>
            )}
          </div>

          {/* AC-6.1: badges on the order confirmation page, flag-gated. */}
          {config.apps_live && (
            <div className="rounded-[10px] border border-line bg-panel p-5">
              <StoreBadges label="Track this on your phone" />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * How the customer reached the studio, on the order they placed.
 *
 * Spec 006 FR-610 in the small. The form asks for this on every manual order,
 * and a field that is asked for and never shown back is a field people stop
 * filling in honestly. It is shown to internal readers only: the customer knows
 * how they got here, and to them it reads as a note somebody made about them.
 *
 * The LABEL comes from the database rather than from a copy of the seed in this
 * file. Adding "TikTok" next year is then one row, not a row and an edit here
 * that somebody forgets. Until the list arrives, the code itself is shown, so
 * the line is never blank.
 */
function CameInFrom({ code }: { code: string }) {
  const { data } = useQuery(orderSourcesQuery);
  const label = (data?.sources ?? []).find((s) => s.code === code)?.label;
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[13px] text-muted">Came in from</dt>
      <dd className="text-[13px] text-muted">{label ?? code.replace('_', ' ')}</dd>
    </div>
  );
}

/**
 * The product photo on an order line, 96 px, and the viewer on a click.
 * Spec 008 FR-809 and FR-801. The drawn garment stays for a line with no
 * product, which is every line imported from Shopify's history.
 */
function LinePhoto({ photos, title }: { photos: Photo[]; title: string }) {
  const [open, setOpen] = useState(false);
  const first = photos[0]!;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Show ${title} large`}
        className="relative h-24 w-24 shrink-0 overflow-hidden rounded-[10px] border border-line bg-raised"
      >
        <img src={first.url} alt={first.alt} loading="lazy" className="h-full w-full object-cover" />
        {photos.length > 1 && (
          <span className="absolute bottom-1 right-1 rounded-[4px] bg-ink/80 px-1.5 text-[11px] text-panel">
            {photos.length}
          </span>
        )}
      </button>
      {open && <PhotoViewer photos={photos} title={title} onClose={() => setOpen(false)} />}
    </>
  );
}
